import type { Db } from "./client";
import { getUserId } from "./client";
import {
  taskCreateSchema,
  taskUpdateSchema,
  taskCompletionCreateSchema,
  type TaskCreateInput,
  type TaskUpdateInput,
  type TaskCompletionCreateInput,
} from "./schemas";
import { ValidationError, NotFoundError, throwIfPgError } from "./errors";
import type {
  TaskRow,
  TaskInsert,
  TaskCompletionRow,
  TaskCompletionInsert,
} from "@garden/types/db";

export async function createTask(
  client: Db,
  input: TaskCreateInput,
): Promise<TaskRow> {
  const parsed = taskCreateSchema.safeParse(input);
  if (!parsed.success) {
    throw new ValidationError("invalid task input", parsed.error);
  }
  const userId = await getUserId(client);
  const { data, error } = await client
    .from("task")
    .insert({ ...parsed.data, user_id: userId } as TaskInsert) // `as` cast sanctioned at Supabase boundary per spec §5
    .select("*")
    .single();
  throwIfPgError(error);
  return data as TaskRow; // `as` cast sanctioned at Supabase boundary per spec §5
}

export async function getTask(client: Db, id: string): Promise<TaskRow> {
  const { data, error } = await client
    .from("task")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  throwIfPgError(error);
  if (!data) throw new NotFoundError("task", id);
  return data as TaskRow; // `as` cast sanctioned at Supabase boundary per spec §5
}

export async function updateTask(
  client: Db,
  id: string,
  patch: TaskUpdateInput,
): Promise<TaskRow> {
  const parsed = taskUpdateSchema.safeParse(patch);
  if (!parsed.success) {
    throw new ValidationError("invalid task patch", parsed.error);
  }
  const { data, error } = await client
    .from("task")
    .update(parsed.data as Partial<Omit<TaskInsert, "user_id">>) // `as` cast sanctioned at Supabase boundary per spec §5
    .eq("id", id)
    .select("*")
    .maybeSingle();
  throwIfPgError(error);
  if (!data) throw new NotFoundError("task", id);
  return data as TaskRow; // `as` cast sanctioned at Supabase boundary per spec §5
}

export async function deleteTask(client: Db, id: string): Promise<void> {
  const { error } = await client.from("task").delete().eq("id", id);
  throwIfPgError(error);
}

export async function listTasks(
  client: Db,
  gardenId?: string,
): Promise<TaskRow[]> {
  let q = client.from("task").select("*");
  if (gardenId) q = q.eq("garden_id", gardenId);
  const { data, error } = await q.order("due_date", {
    ascending: true,
    nullsFirst: false,
  });
  throwIfPgError(error);
  return (data ?? []) as TaskRow[];
}

export async function listTasksForPlant(
  client: Db,
  plantId: string,
): Promise<TaskRow[]> {
  const { data, error } = await client
    .from("task")
    .select("*")
    .eq("plant_id", plantId)
    .order("due_date", { ascending: true, nullsFirst: false });
  throwIfPgError(error);
  return (data ?? []) as TaskRow[];
}

/** Tasks with `due_date` within ±N days of today and status='pending'.
 *  Used by the mobile /today screen (spec §5.5: ±7 days). */
export async function listActiveTasks(
  client: Db,
  windowDays = 7,
  gardenId?: string,
): Promise<TaskRow[]> {
  const today = new Date();
  const start = new Date(today);
  start.setDate(today.getDate() - windowDays);
  const end = new Date(today);
  end.setDate(today.getDate() + windowDays);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  let q = client
    .from("task")
    .select("*")
    .eq("status", "pending")
    .gte("due_date", fmt(start))
    .lte("due_date", fmt(end));
  if (gardenId) q = q.eq("garden_id", gardenId);
  const { data, error } = await q.order("due_date", { ascending: true });
  throwIfPgError(error);
  return (data ?? []) as TaskRow[];
}

/** Insert a task_completion row and flip the task to 'complete'. Performed
 *  in two round-trips; if the second fails we still leave the completion row,
 *  which is the correct semantic (the work happened — we just couldn't
 *  update the task status). */
export async function completeTask(
  client: Db,
  input: TaskCompletionCreateInput,
): Promise<TaskCompletionRow> {
  const parsed = taskCompletionCreateSchema.safeParse(input);
  if (!parsed.success) {
    throw new ValidationError("invalid completion input", parsed.error);
  }
  const userId = await getUserId(client);
  const { data, error } = await client
    .from("task_completion")
    .insert({ ...parsed.data, user_id: userId } as TaskCompletionInsert) // `as` cast sanctioned at Supabase boundary per spec §5
    .select("*")
    .single();
  throwIfPgError(error);
  const status = parsed.data.is_skip ? "skipped" : "complete";
  const { error: updErr } = await client
    .from("task")
    .update({ status })
    .eq("id", parsed.data.task_id);
  throwIfPgError(updErr);
  return data as TaskCompletionRow; // `as` cast sanctioned at Supabase boundary per spec §5
}

export async function listCompletionsForTask(
  client: Db,
  taskId: string,
): Promise<TaskCompletionRow[]> {
  const { data, error } = await client
    .from("task_completion")
    .select("*")
    .eq("task_id", taskId)
    .order("completed_date", { ascending: false });
  throwIfPgError(error);
  return (data ?? []) as TaskCompletionRow[];
}
